'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

export interface StickyNavSection {
  id: string;       // DOM element id to scroll to
  label: string;    // Tab label
  show: boolean;    // Whether to show this tab
}

interface StickyNavProps {
  sections: StickyNavSection[];
}

const StickyNav: React.FC<StickyNavProps> = ({ sections }) => {
  const [activeId, setActiveId] = useState<string>('');
  const [isSticky, setIsSticky] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const visibleSections = sections.filter((s) => s.show);

  // Track which section is being read: the last one whose top has passed
  // the reading line under the sticky bars. Above the first section (the
  // page top) the first tab is active. An IntersectionObserver band kept the
  // last tab it saw, so a page scrolled back to the top still said "FAQ".
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ids = visibleSections.map((s) => s.id);
    let raf = 0;
    const update = () => {
      raf = 0;
      const line = 140;
      let current = '';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (!current) current = id;
        if (el.getBoundingClientRect().top <= line) current = id;
      }
      setActiveId(current);
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  // Track sticky state using a sentinel element
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  const handleClick = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const navHeight = 56; // approximate sticky nav height
      const y = el.getBoundingClientRect().top + window.scrollY - navHeight - 8;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  if (visibleSections.length < 2) return null;

  return (
    <>
      {/* Sentinel — when this scrolls out of view, nav becomes sticky */}
      <div ref={sentinelRef} className="h-0" />

      <nav
        ref={navRef}
        aria-label="Page sections"
        className={`sticky top-0 z-40 bg-white transition-shadow duration-200 ${
          isSticky ? 'shadow-md' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            role="tablist"
            aria-label="Campsite sections"
            className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-0.5"
          >
            {visibleSections.map((section) => {
              const isActive = activeId === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={section.id}
                  onClick={() => handleClick(section.id)}
                  className={`relative whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-[#1558b0]'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {section.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-t bg-[#1558b0]" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
};

export default StickyNav;
